import * as React from 'react';
import { css } from '@emotion/react';
import { useHoneycombTheme } from '../../theming';
import { FixMeLater } from '../../utils';
import { CheckboxProps } from '../Checkbox/Checkbox';
import { RadioProps } from '../Radio/Radio';
import { SwitchProps } from '../Switch/Switch';
import { Styler } from '../Styler/Styler';
import { Body, BodyProps } from '../Typography/Body/Body';

export type CheckboxPosition = 'start' | 'end';
export type CheckboxAlignment = 'center' | 'top';

export type SwitchControlLabelProps = {
    /**
     * Optional label content.
     */
    children?: React.ReactNode;

    /**
     * The vertical alignment of the label in relation to the control.
     * @default center
     */
    align?: CheckboxAlignment;

    /**
     * The child switch control.
     */
    control: React.ReactElement<CheckboxProps> | React.ReactElement<RadioProps> | React.ReactElement<SwitchProps>;

    /**
     * The text to be displayed alongside the control. If children are also provided then the label will be applied as an aria-label.
     */
    label: string;

    /**
     * Additional properties applied to the label component.
     */
    LabelProps?: BodyProps;

    /**
     * The position of the label in relation to the control.
     * @default end
     */
    placement?: CheckboxPosition;

    /**
     * Additional properties applied to the label component.
     */
    spacing?: number;
};

export const SwitchControlLabel = React.forwardRef(function SwitchControlLabel(
    props: SwitchControlLabelProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const { align = 'center', children, control, label, LabelProps, placement = 'end', spacing = 3, ...other } = props;

    const disabled = control.props.disabled || false;

    const { theme } = useHoneycombTheme();

    const styles = css`
        display: inline-flex;
        align-items: ${align === 'top' ? 'flex-start' : 'center'};
        flex-wrap: nowrap;
        cursor: pointer;
        ${placement === 'start' && 'flex-direction: row-reverse;'}
        & img {
            vertical-align: middle;
        }
    `;

    const labelStyles = disabled
        ? css`
              opacity: ${theme.interaction.disabledOpacity};
          `
        : null;

    return (
        <label ref={ref} css={styles} {...other}>
            {children ? React.cloneElement(control, { 'aria-label': label }) : control}
            <Styler css={labelStyles} {...{ [placement === 'start' ? 'mr' : 'ml']: spacing }}>
                {children}
                {label && !children && (
                    <Body css={labelStyles} component="div" {...LabelProps}>
                        {label}
                    </Body>
                )}
            </Styler>
        </label>
    );
});
